import React, { useMemo, useState } from 'react';
import { getShowCount } from '@/pages/opinion/utils';
import KeyWordCommon from '@/pages/opinion/components/KeywordCommon';
import BubbleCommon, {
  keywordTipsText,
  newsEngagementTips,
  newsTotalArticlesTips,
  newsUVPMTips,
  TipsMapType,
} from '@/pages/opinion/components/BubbleCommon'; 
import {
  brandHealthFormat,
  sentimentFormat,
} from '@/pages/opinion/utils/value';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import logoDatabrain from '@/pages/opinion/assets/svg/logo-databrain.svg';
import logoLevel from '@/pages/opinion/assets/svg/logo-level-infinite.svg';
import CardSentiment from '@op/pages/accounts/widgets/AccountsCard/Sentiment';
import useLanguage from '@op/hooks/useLanguage';
import TranslateSelect from '@op/components/SelectComponents/TranslateSelect';
import { languageSelectOptions } from '@op/components/FilterBoxCommon/constant';
import './index.scss'; 

type KeyMetricsType = {
  key: any;
  text: string;
  subText?: string;
  icon?: string;
  tips?: TipsMapType;
  format?: (value: number) => string | number;
};

const brandHealthKeyMetrics: KeyMetricsType = {
  key: 'rating_avg',
  text: 'Brand Health',
  subText: '-100~100',
  format: brandHealthFormat,
};
const sentimentKeyMetrics: KeyMetricsType = {
  key: 'rating_avg',
  text: 'Sentiment',
  subText: 'Up to 5',
  format: sentimentFormat,
};

const keyMetricsMap: {
  [key in 'news' | 'default' | 'game_store']: Array<KeyMetricsType>;
} = {
  news: [
    { key: 'total', text: 'Total Articles', tips: newsTotalArticlesTips },
    {
      key: 'uvpm',
      text: 'Potential Reach',
      icon: logoLevel,
      tips: newsUVPMTips,
    },
    {
      key: 'engagement',
      text: 'Engagement',
      icon: logoDatabrain,
      tips: newsEngagementTips,
    },
  ],
  game_store: [{ key: 'total', text: 'Mentions' }],
  default: [{ key: 'total', text: 'Mentions' }],
};

export default function KeywordBox({
  data = {
    neutral_review: 0,
    good_review: 0,
    bad_review: 0,
    total: 0,
    rating_avg: 0,
  },
  keywords = [],
  keywordChange,
  setSentimentSelect,
  stabled = false,
  autoSelectFirst = false,
  sentimentSelect,
  interactable = false,
  tableTotal = 0,
  commentLoading = false,
  GptBtnElement = null,
  needBrandHealth = false,
  defaultSelectedKeyword = [],
  keyMetricsKey = 'default',
  showIEGGPr,
}: {
  stabled?: boolean;
  autoSelectFirst?: boolean;
  data: {
    neutral_review: number;
    good_review: number;
    bad_review: number;
    total: number;
    rating_avg: number;
  };
  keywords: Array<any>;
  keywordChange?: (keywords: {
    parent: Array<string>;
    children: Array<string>;
    keywordRender: {
      keyword: Array<string>;
      keyword_cn: Array<string>;
      keyword_en: Array<string>;
    };
  }) => void;
  commentLoading?: boolean;
  interactable?: boolean;
  setSentimentSelect?: any;
  sentimentSelect?: 1 | 3 | 5 | null;
  tableTotal?: number;
  defaultSelectedKeyword?: Array<string>;
  needBrandHealth?: boolean;
  keyMetricsKey?: 'default' | 'news' | 'game_store';
  showIEGGPr?: boolean;
  GptBtnElement?: JSX.Element | any;
}) {
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const [language, setLanguage] = useState('origin');
  // console.log('KeywordBox-debug', 'showAigcBtn', showAigcBtn);
  const { Sentiment_Rating, Keyword_Color } = useGetSingePageSourceRule({
    keys: ['Sentiment_Rating', 'Keyword_Color'],
  });

  const keyMetricsList = useMemo(() => {
    let options = keyMetricsMap[keyMetricsKey];
    if (Sentiment_Rating) {
      return options;
    }
    const item = needBrandHealth ? brandHealthKeyMetrics : sentimentKeyMetrics;
    if (!showIEGGPr) {
      options = options?.filter((val) => val.key !== 'uvpm');
    }
    return [...options, item];
  }, [Sentiment_Rating]);

  return (
    <div className="feeds-keywordbox" style={{ position: 'relative' }}>
      {GptBtnElement}

      <div className="feeds-keywordbox-hd">
        {keyMetricsList.map(
          ({ key, subText, text, icon, tips, format = getShowCount }) => {
            return (
              <div className="keywordbox-hd-itembox" key={key}>
                <div className="keywordbox-hd-tit">
                  {icon && (
                    <img src={icon} className="keywordbox-hd-tit-icon" />
                  )}
                  {formatMessage({ id: text })}
                  {tips && (
                    <BubbleCommon placement="top" tipsMap={tips}></BubbleCommon>
                  )}
                </div>
                <div className="keywordbox-hd-text">
                  {format(data?.[key] || 0)}
                </div>
                {subText && (
                  <div className="keywordbox-hd-desc">
                    {formatMessage({ id: subText })}
                  </div>
                )}
              </div>
            );
          },
        )}

        {!Sentiment_Rating && (
          <div className="keywordbox-hd-itembox keywordbox-hd-itembox--sentiment">
            <CardSentiment
              sentimentsData={data}
              className={`keywordbox-hd-itembox ${
                ['news', 'game_store'].includes(keyMetricsKey) ? '--flex' : ''
              }`}
              hideOptions={['news', 'game_store'].includes(keyMetricsKey)}
            />
          </div>
        )}
      </div>
      <div className="feeds-keywordbox-bd">
        <div className="keywordbox-title">
          <span>{formatMessage({ id: 'Keywords (Mentions)' })}</span>
          {!Keyword_Color && (
            <BubbleCommon tipsMap={keywordTipsText}></BubbleCommon>
          )}
          <TranslateSelect
            langOptions={formatOptionsMessage(languageSelectOptions)}
            lang={language}
            isSingle={true}
            isMatchButtonWidth={false}
            getChange={(e: any) => {
              setLanguage(e);
            }}
            defaultValueText={formatMessage({ id: 'Language' })}
            originalText={'Original Language'}
          />
        </div>
        <KeyWordCommon
          keywords={keywords}
          commentLoading={commentLoading}
          keywordChange={keywordChange}
          language={language}
          stabled={stabled}
          needBrandHealth={needBrandHealth}
          defaultSelectedKeyword={defaultSelectedKeyword}
          autoSelectFirst={autoSelectFirst}
          tableTotal={tableTotal}
        ></KeyWordCommon>
      </div>
    </div>
  );
}

// 冒泡排序
